<script setup>
// Os destinatários de uma execução.
//
// A pergunta aqui é "esse disparo chegou em quem?", então cada linha carrega o
// desfecho do envio (ENVIADO/ERRO/PULADO) e, quando a mensagem existe no
// PowerChat, o estado de entrega que a Meta devolveu depois — `sent` que nunca
// vira `delivered` é entrega barrada, e é a diferença entre "mandamos" e
// "chegou".
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import outboundsAPI from 'dashboard/api/outbounds';
import {
  fullTimestamp,
  statusTone,
  timeLabel,
  whenLabel,
} from '../helpers/outboundHistory';

const props = defineProps({
  lote: { type: Object, required: true },
});

const emit = defineEmits(['back', 'open-conversation']);

const { t } = useI18n();
const store = useStore();
const accountId = store.getters.getCurrentAccountId;

const disparos = ref([]);
const loading = ref(false);

const toneClasses = {
  teal: 'bg-n-teal-3 text-n-teal-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
};

const agente = computed(
  () => props.lote.chatbot_name || `#${props.lote.chatbot_id}`
);

async function load() {
  loading.value = true;
  try {
    const payload = await outboundsAPI.historyLote(
      accountId,
      props.lote.id_lote
    );
    disparos.value = payload?.disparos || [];
  } catch (error) {
    useAlert(error?.response?.data?.error || t('OUTBOUNDS.HISTORY.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

function entregaLabel(post) {
  const entrega = post.conversa?.entrega;
  if (!entrega) return '';
  return t(`OUTBOUNDS.HISTORY.DELIVERY.${entrega.toUpperCase()}`);
}

watch(() => props.lote.id_lote, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <button
        type="button"
        class="flex items-center gap-1 mb-2 text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="emit('back')"
      >
        <i class="i-lucide-arrow-left size-4" />
        {{ t('OUTBOUNDS.HISTORY.BACK_EXECUTIONS') }}
      </button>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ lote.template_name }}
      </h2>
      <p class="text-sm text-n-slate-10" :title="fullTimestamp(lote.inicio)">
        {{ agente }}
        <span v-if="lote.agent_phone" class="tabular-nums">
          · {{ lote.agent_phone }}
        </span>
        · {{ whenLabel(lote.inicio, t) }} {{ timeLabel(lote.inicio) }}
        <span v-if="lote.segmento"> · {{ lote.segmento }}</span>
      </p>
    </div>

    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.LOADING') }}
    </p>

    <p v-else-if="!disparos.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.HISTORY.PERSON_EMPTY') }}
    </p>

    <div v-else class="overflow-x-auto border rounded-lg border-n-weak">
      <table class="w-full text-sm">
        <thead class="bg-n-alpha-1 text-n-slate-11">
          <tr>
            <th class="px-3 py-2 font-medium text-left">
              {{ t('OUTBOUNDS.HISTORY.COLUMNS.PERSON') }}
            </th>
            <th class="px-3 py-2 font-medium text-left">
              {{ t('OUTBOUNDS.HISTORY.COLUMNS.PHONE') }}
            </th>
            <th class="px-3 py-2 font-medium text-left">
              {{ t('OUTBOUNDS.HISTORY.COLUMNS.OUTCOME') }}
            </th>
            <th class="px-3 py-2 font-medium text-left">
              {{ t('OUTBOUNDS.HISTORY.COLUMNS.CONVERSATION') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="post in disparos"
            :key="post.id"
            class="border-t border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">
              {{ post.nome || t('OUTBOUNDS.HISTORY.NO_NAME') }}
              <span class="block text-xs text-n-slate-10">
                {{ timeLabel(post.sent_at) }}
              </span>
            </td>
            <td class="px-3 py-2 tabular-nums text-n-slate-11">
              {{ post.phone || post.phone_original }}
            </td>
            <td class="px-3 py-2">
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="toneClasses[statusTone(post.status)]"
              >
                {{ post.status }}
              </span>
              <span
                v-if="entregaLabel(post)"
                class="block mt-1 text-xs text-n-slate-10"
              >
                {{ entregaLabel(post) }}
              </span>
              <span v-if="post.erro" class="block mt-1 text-xs text-n-ruby-11">
                {{ post.erro }}
              </span>
            </td>
            <td class="px-3 py-2">
              <button
                v-if="post.conversa && post.conversa.id"
                type="button"
                class="flex items-center gap-1 text-xs text-n-brand hover:underline"
                @click="
                  emit('open-conversation', {
                    nome: post.nome,
                    conversa: post.conversa,
                  })
                "
              >
                <i class="i-lucide-messages-square size-4" />
                {{
                  post.conversa.inbox_name ||
                  t('OUTBOUNDS.HISTORY.CONVERSATION.OPEN')
                }}
              </button>
              <span v-else class="text-xs text-n-slate-10">
                {{ t('OUTBOUNDS.HISTORY.CONVERSATION.NONE') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
